import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Send, Loader2, Sparkles, ArrowLeft, Clock, X, CalendarDays } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const INTRO = "Hi, I'm Roger 👋 Tell me what kind of company you run — for example \"kitchen cabinet refacing\", \"roofing\", or \"flooring\" — and I'll suggest 5 categories to get your library started. You can also ask me to rename, move, recolor or reorganize anything.";

const todayStr = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD (local)
const prettyDate = (d) => {
  const dt = new Date(d + "T00:00:00");
  const t = todayStr();
  if (d === t) return "Today";
  const y = new Date(Date.now() - 86400000).toLocaleDateString("en-CA");
  if (d === y) return "Yesterday";
  return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

export default function Art() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const active = company?.subscription_active;
  const today = todayStr();

  const [msgs, setMsgs] = useState([{ role: "art", text: INTRO }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [dates, setDates] = useState(null);
  const [viewing, setViewing] = useState(null); // null = today (live); else a past date
  const [histMsgs, setHistMsgs] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy, viewing, histMsgs]);

  // load today's saved thread on mount
  useEffect(() => {
    api.get(`/roger/history/${today}`).then(({ data }) => {
      if (data && data.length) setMsgs([{ role: "art", text: INTRO }, ...data.map((m) => ({ role: m.role, text: m.text }))]);
    }).catch(() => {});
  }, [today]);

  const openHistory = useCallback(async () => {
    setShowHist(true);
    try { const { data } = await api.get("/roger/history/dates"); setDates(data); }
    catch { setDates([]); }
  }, []);

  const viewDay = async (d) => {
    setShowHist(false);
    if (d === today) { setViewing(null); return; }
    setViewing(d); setHistLoading(true); setHistMsgs([]);
    try { const { data } = await api.get(`/roger/history/${d}`); setHistMsgs(data.map((m) => ({ role: m.role, text: m.text }))); }
    catch { setHistMsgs([]); }
    setHistLoading(false);
  };

  const send = async () => {
    const cmd = input.trim();
    if (!cmd || busy) return;
    setMsgs((m) => [...m, { role: "user", text: cmd }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/library/assistant", { command: cmd, client_date: today });
      const suffix = data.count ? ` (${data.count} change${data.count === 1 ? "" : "s"} applied)` : "";
      setMsgs((m) => [...m, { role: "art", text: (data.reply || "Done.") + suffix }]);
      if (data.categories_changed) setMsgs((m) => [...m, { role: "art", text: "✅ I updated your categories — head back to the Library tab and refresh to see them." }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "art", text: apiError(e.response?.data?.detail) || "I couldn't do that. Try rephrasing." }]);
    } finally { setBusy(false); }
  };

  const shown = viewing ? histMsgs : msgs;

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-[var(--bone)]" data-testid="art-page">
      <header className="flex shrink-0 items-center gap-3 border-b border-[var(--line)] bg-[var(--ink)] px-4 py-4 text-white"
        style={{ paddingTop: "calc(1rem + env(safe-area-inset-top))" }}>
        <button data-testid="art-back" onClick={() => navigate("/library")} aria-label="Back to library"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-white/10 active:scale-95">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--brand)]"><Bot className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg leading-none">Meet Roger</p>
          <p className="text-[11px] text-white/60">Your AI library assistant</p>
        </div>
        <button data-testid="art-history-btn" onClick={openHistory} aria-label="Chat history"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-white/10 active:scale-95">
          <Clock className="h-5 w-5" />
        </button>
      </header>

      {viewing && (
        <div data-testid="art-viewing-banner" className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--line)] bg-[var(--brand)]/10 px-5 py-2.5 text-sm">
          <span className="flex items-center gap-2 font-medium text-[var(--ink)]"><CalendarDays className="h-4 w-4 text-[var(--brand)]" /> Viewing {prettyDate(viewing)}</span>
          <button data-testid="art-back-to-today" onClick={() => setViewing(null)} className="font-semibold text-[var(--brand)]">Back to today</button>
        </div>
      )}

      <div className="mx-auto flex w-full max-w-2xl min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-5">
        {histLoading ? (
          <div className="self-center pt-6 text-sm text-[var(--ink-soft)]"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          shown.map((m, i) => (
            <div key={i} data-testid={`art-msg-${m.role}`} className={`max-w-[88%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${m.role === "user" ? "self-end bg-[var(--ink)] text-white" : "self-start border border-[var(--line)] bg-white"}`}>
              {m.text}
            </div>
          ))
        )}
        {busy && !viewing && <div className="self-start rounded-2xl border border-[var(--line)] bg-white px-4 py-3.5"><Loader2 className="h-4 w-4 animate-spin text-[var(--brand)]" /></div>}
        <div ref={endRef} />
      </div>

      {!viewing && (
        <div className="shrink-0 border-t border-[var(--line)] bg-white p-4" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
          <div className="mx-auto flex w-full max-w-2xl items-end gap-2">
            {!active ? (
              <p className="flex-1 rounded-xl bg-[var(--bone)] px-4 py-3 text-sm text-[var(--ink-soft)]">
                <Sparkles className="mr-1 inline h-4 w-4 text-[var(--brand)]" /> Subscribe to chat with Roger and auto-build your library.
              </p>
            ) : (
              <>
                <textarea data-testid="art-input" value={input} onChange={(e) => setInput(e.target.value)} rows={1}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="e.g. I run a kitchen cabinet refacing company"
                  className="max-h-32 flex-1 resize-none rounded-2xl border border-[var(--line)] bg-[var(--bone)] px-4 py-3.5 text-base outline-none focus:border-[var(--brand)]" />
                <button data-testid="art-send" onClick={send} disabled={busy}
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-white transition-colors hover:bg-[var(--brand-dark)] active:scale-95 disabled:opacity-50">
                  <Send className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showHist && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setShowHist(false)} data-testid="art-history-overlay">
          <div className="flex h-full w-full max-w-sm flex-col bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}
            style={{ paddingTop: "env(safe-area-inset-top)" }}>
            <div className="flex items-center justify-between border-b border-[var(--line)] px-5 py-4">
              <p className="font-display text-lg">Chat history</p>
              <button data-testid="art-history-close" onClick={() => setShowHist(false)} className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-[var(--bone)]"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <p className="px-2 pb-2 text-xs text-[var(--ink-soft)]">Roger keeps a fresh thread each day — nothing is ever deleted.</p>
              {dates === null ? (
                <p className="p-4 text-sm text-[var(--ink-soft)]"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…</p>
              ) : dates.length === 0 ? (
                <p className="p-4 text-sm text-[var(--ink-soft)]">No history yet. Start chatting with Roger!</p>
              ) : (
                dates.map((d) => (
                  <button key={d.date} data-testid={`art-date-${d.date}`} onClick={() => viewDay(d.date)}
                    className={`mb-1.5 flex w-full items-center justify-between rounded-xl px-4 py-3 text-left transition-colors hover:bg-[var(--bone)] ${(viewing === d.date || (!viewing && d.date === today)) ? "bg-[var(--brand)]/10" : ""}`}>
                    <span className="flex items-center gap-2.5">
                      <CalendarDays className="h-4 w-4 text-[var(--brand)]" />
                      <span className="text-sm font-medium">{prettyDate(d.date)}</span>
                    </span>
                    <span className="text-xs text-[var(--ink-soft)]">{d.count} msg{d.count === 1 ? "" : "s"}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
